/**
 * Хелперы раздела push-рассылок.
 *
 * Права, клиентская проверка готовности, форматтеры и выгрузка отчёта.
 * Всё чистые функции — их можно покрыть тестами и переиспользовать
 * в виджетах, не таща за собой React.
 */

import type { User } from '../../../types/interfaces';
import {
  ENGAGEMENT_MODE_OPTIONS,
  PUSH_ERROR_CODES,
} from '../../../types/marketingPushInterfaces';
import type {
  BadgeTone,
  DeliveryStatus,
  Platform,
  PushAppTarget,
  PushCampaignDraft,
  PushDeliveryErrorRow,
} from '../../../types/marketingPushInterfaces';
import { DELIVERY_STATUS_LABEL } from '../../../types/marketingPushInterfaces';
import { toPlainText } from './richtext';

export * from './richtext';

/* -------------------------------------------------------------------------- */
/* Права                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Права раздела.
 *
 * Проверка по действиям из `me.permissions`, а не по именам групп: имена
 * ролей MARKETING_HEAD и MARKETER могут измениться, а состав прав у группы —
 * это конфигурация ЛК. Формат совпадает с тем, что строит
 * `defineAbilitiesFor`: ресурс → список действий.
 *
 * Суперпользователь обходит проверку, потому что `permissions` в
 * `/v1/users/me/` собирается из групп и личных прав, а не через
 * `get_all_permissions()` — у суперюзера список приходит неполным.
 * `defineAbilitiesFor` поступает так же, выдавая ему `manage all`.
 *
 * Всё это только про интерфейс: скрытая кнопка не защита, те же права
 * проверяются на бэкенде при каждом запросе.
 */
export type PushAction = 'view' | 'add' | 'change' | 'delete' | 'send';
export type PushSubject = 'campaign' | 'template' | 'notificationtype';

export function canPush(
  me: User | null | undefined,
  action: PushAction,
  subject: PushSubject = 'campaign',
): boolean {
  if (!me) return false;
  if (me.is_superuser) return true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const permissions = (me as any).permissions as Record<string, string[]> | undefined;
  return Boolean(permissions?.[subject]?.includes(action));
}

export interface PushPermissions {
  view: boolean;
  create: boolean;
  edit: boolean;
  remove: boolean;
  /** Массовая отправка и планирование. Отдельно от edit намеренно:
   *  подготовить рассылку и отправить её — разные полномочия. */
  send: boolean;
  manageTemplates: boolean;
  manageTypes: boolean;
}

export function pushPermissions(me: User | null | undefined): PushPermissions {
  return {
    view: canPush(me, 'view'),
    create: canPush(me, 'add'),
    edit: canPush(me, 'change'),
    remove: canPush(me, 'delete'),
    send: canPush(me, 'send'),
    manageTemplates: canPush(me, 'change', 'template'),
    manageTypes: canPush(me, 'change', 'notificationtype'),
  };
}

export function canAccessPushSection(me: User | null | undefined): boolean {
  return canPush(me, 'view', 'campaign');
}

/* -------------------------------------------------------------------------- */
/* Платформы                                                                   */
/* -------------------------------------------------------------------------- */

export function platformLabel(platform: Platform): string {
  return platform === 'ios' ? 'iOS' : 'Android';
}

/** Платформы выбранных приложений. Нужны, чтобы не предупреждать про iOS,
 *  когда рассылка идёт только на Android. */
export function platformsOf(selectedCodes: string[], allTargets: PushAppTarget[]): Platform[] {
  const platforms = new Set<Platform>();
  allTargets.forEach((target) => {
    if (selectedCodes.includes(target.code)) platforms.add(target.platform);
  });
  return Array.from(platforms);
}

/* -------------------------------------------------------------------------- */
/* Проверка готовности                                                         */
/* -------------------------------------------------------------------------- */

export interface DraftProblem {
  /** Код языка, если проблема в конкретной языковой версии. */
  language?: string;
  message: string;
}

/**
 * Проверка черновика до обращения к серверу.
 *
 * Повторяет серверные правила не из недоверия, а чтобы вкладка языка
 * подсвечивалась незаполненной сразу, а не после нажатия «Отправить».
 * Источник истины — сервер: перед отправкой он проверяет заново.
 */
export function validatePushDraft(draft: PushCampaignDraft | null): DraftProblem[] {
  if (!draft) return [{ message: 'Черновик не загружен.' }];

  const problems: DraftProblem[] = [];

  if (!draft.notification_type) problems.push({ message: 'Выберите тип уведомления.' });
  if (!draft.targets.length) problems.push({ message: 'Выберите хотя бы одно приложение.' });
  if (!draft.contents.length) {
    problems.push({ message: 'Выберите хотя бы один язык и заполните контент.' });
  }

  draft.contents.forEach((content) => {
    const language = content.language;
    if (!content.push_title.trim()) {
      problems.push({ language, message: 'Заполните заголовок пуша.' });
    }
    if (!content.push_body.trim()) {
      problems.push({ language, message: 'Заполните текст пуша.' });
    }
    if (!content.inapp_title.trim()) {
      problems.push({ language, message: 'Заполните заголовок внутри приложения.' });
    }
    if (!toPlainText(content.inapp_body).trim()) {
      problems.push({ language, message: 'Заполните текст внутри приложения.' });
    }
    if (content.button_label.trim() && !content.button_url.trim()) {
      problems.push({ language, message: 'У кнопки есть текст, но нет ссылки.' });
    }
    if (content.button_url.trim() && !content.button_label.trim()) {
      problems.push({ language, message: 'У кнопки есть ссылка, но нет текста.' });
    }
  });

  if (draft.engagement_mode === 'more' || draft.engagement_mode === 'less') {
    if (draft.engagement_days === null) {
      problems.push({ message: 'Укажите количество дней для фильтра по активности.' });
    }
  }
  if (draft.engagement_mode === 'between') {
    if (draft.engagement_from_days === null || draft.engagement_to_days === null) {
      problems.push({ message: 'Укажите обе границы диапазона дней.' });
    }
  }

  return problems;
}

/** Языки с незаполненными полями — для подсветки вкладок. */
export function languagesWithProblems(draft: PushCampaignDraft | null): Set<string> {
  return new Set(
    validatePushDraft(draft)
      .map((problem) => problem.language)
      .filter((language): language is string => Boolean(language)),
  );
}

export function engagementHint(mode: string): string {
  return ENGAGEMENT_MODE_OPTIONS.find((option) => option.value === mode)?.hint ?? '';
}

/* -------------------------------------------------------------------------- */
/* Коды ошибок                                                                 */
/* -------------------------------------------------------------------------- */

export function describeErrorCode(code: string): {
  label: string;
  hint: string;
  tone: BadgeTone;
} {
  return (
    PUSH_ERROR_CODES[code] ?? {
      label: code || 'Неизвестная ошибка',
      hint: 'Код не распознан. Покажите разработчику.',
      tone: 'neutral',
    }
  );
}

/** Классы Tailwind для бейджа тона. Держим здесь, чтобы палитра статусов
 *  и палитра ошибок не разъезжались по компонентам. */
export const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'bg-gray-100 text-gray-600',
  accent: 'bg-blue-50 text-blue-700',
  success: 'bg-green-50 text-green-700',
  warning: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-700',
  info: 'bg-sky-50 text-sky-700',
};

/* -------------------------------------------------------------------------- */
/* Форматирование                                                              */
/* -------------------------------------------------------------------------- */

export function formatPushDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatPushNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('ru-RU');
}

/** Длина в кодовых точках: эмодзи не должны считаться за два символа. */
export function charLength(value: string): number {
  return Array.from(value ?? '').length;
}

/* -------------------------------------------------------------------------- */
/* Выгрузка отчёта                                                             */
/* -------------------------------------------------------------------------- */

const CSV_DELIMITER = ';';
const CSV_BOM = '\uFEFF';

/**
 * CSV отчёта об ошибках.
 *
 * Две детали, без которых файл открывается неправильно именно в русском
 * Excel, а открывать его будет маркетинг:
 *
 *   BOM в начале — иначе Excel читает UTF-8 как ANSI и вместо русских букв
 *   получается каша.
 *
 *   Точка с запятой как разделитель — в локали с запятой в роли десятичного
 *   разделителя Excel не разбирает CSV с запятыми и складывает всю строку
 *   в первую колонку.
 */
export function buildErrorsCsv(rows: PushDeliveryErrorRow[]): string {
  const header = [
    'Установка',
    'Приложение',
    'Язык',
    'Статус',
    'Код ошибки',
    'Причина',
    'Подробности',
    'Попыток',
  ];

  const lines = rows.map((row) => {
    const meta = describeErrorCode(row.error_code);
    return [
      String(row.installation_id),
      row.app_target__code,
      row.language__code,
      DELIVERY_STATUS_LABEL[row.status as DeliveryStatus] ?? row.status,
      row.error_code,
      meta.label,
      row.error_message,
      String(row.attempts),
    ].map(escapeCsvCell);
  });

  return (
    CSV_BOM + [header.map(escapeCsvCell), ...lines].map((cells) => cells.join(CSV_DELIMITER)).join('\r\n')
  );
}

function escapeCsvCell(value: string): string {
  const text = (value ?? '').replace(/\r?\n/g, ' ');
  if (text.includes(CSV_DELIMITER) || text.includes('"')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Без revoke blob висит в памяти до перезагрузки страницы, а отчёт
  // выгружают несколько раз за день.
  URL.revokeObjectURL(url);
}

export function reportFilename(campaignId: number): string {
  return `push-errors-${campaignId}-${new Date().toISOString().slice(0, 10)}.csv`;
}
